import React, { Suspense } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './AppContext';
import { ToastProvider } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';

// === DIRECT IMPORTS: Always-mounted PageLayer pages + critical auth pages ===
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { PracticeHub } from './pages/PracticeHub';
import { Analysis } from './pages/Analysis';
import { Forum } from './pages/Forum';
import { Settings } from './pages/Settings';
import { Signup } from './pages/StaticPages';
import { ResetPassword } from './pages/ResetPassword';
import { VerifyEmail } from './pages/VerifyEmail';

// === LAZY IMPORTS: Sub-route pages loaded on demand ===
const Practice = React.lazy(() => import('./pages/Practice').then(m => ({ default: m.Practice })));
const TopicDetail = React.lazy(() => import('./pages/TopicDetail').then(m => ({ default: m.TopicDetail })));
const Insights = React.lazy(() => import('./pages/Insights').then(m => ({ default: m.Insights })));
const Profile = React.lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const PointsPage = React.lazy(() => import('./pages/PointsPage').then(m => ({ default: m.PointsPage })));
const CheckinPage = React.lazy(() => import('./pages/CheckinPage').then(m => ({ default: m.CheckinPage })));
const PrestigePage = React.lazy(() => import('./pages/PrestigePage').then(m => ({ default: m.PrestigePage })));
const StardustPage = React.lazy(() => import('./pages/StardustPage').then(m => ({ default: m.StardustPage })));
const QuestionCreator = React.lazy(() => import('./pages/QuestionCreator').then(m => ({ default: m.QuestionCreator })));
const DebugQA = React.lazy(() => import('./pages/DebugQA').then(m => ({ default: m.DebugQA })));
const WrongAnswerBook = React.lazy(() => import('./pages/WrongAnswerBook').then(m => ({ default: m.WrongAnswerBook })));
const TextbookViewer = React.lazy(() => import('./pages/TextbookViewer').then(m => ({ default: m.TextbookViewer })));
const ProfileSettings = React.lazy(() => import('./pages/SettingsSubpages').then(m => ({ default: m.ProfileSettings })));
const SecuritySettings = React.lazy(() => import('./pages/SettingsSubpages').then(m => ({ default: m.SecuritySettings })));
const SubscriptionSettings = React.lazy(() => import('./pages/SettingsSubpages').then(m => ({ default: m.SubscriptionSettings })));
const Privacy = React.lazy(() => import('./pages/StaticPages').then(m => ({ default: m.Privacy })));
const Terms = React.lazy(() => import('./pages/StaticPages').then(m => ({ default: m.Terms })));
const Support = React.lazy(() => import('./pages/StaticPages').then(m => ({ default: m.Support })));
const Lobby = React.lazy(() => import('./pages/Lobby').then(m => ({ default: m.Lobby })));
const GameHub = React.lazy(() => import('./pages/GameHub').then(m => ({ default: m.GameHub })));
const SnakeGame = React.lazy(() => import('./pages/SnakeGame').then(m => ({ default: m.SnakeGame })));
const Game2048 = React.lazy(() => import('./pages/Game2048').then(m => ({ default: m.Game2048 })));
const TetrisGame = React.lazy(() => import('./pages/TetrisGame').then(m => ({ default: m.TetrisGame })));
const MatchGameWrapper = React.lazy(() => import('./pages/MatchGame').then(m => ({ default: m.MatchGame })));
const MemoryGame = React.lazy(() => import('./pages/MemoryGame').then(m => ({ default: m.MemoryGame })));
const MinesweeperGame = React.lazy(() => import('./pages/MinesweeperGame').then(m => ({ default: m.MinesweeperGame })));
const NeonKnight = React.lazy(() => import('./pages/NeonKnight').then(m => ({ default: m.NeonKnight })));
const GardenDefenders = React.lazy(() => import('./pages/GardenDefenders').then(m => ({ default: m.GardenDefenders })));
const SkyRoller = React.lazy(() => import('./pages/SkyRoller').then(m => ({ default: m.SkyRoller })));
const BrawlArena = React.lazy(() => import('./pages/BrawlArena').then(m => ({ default: m.BrawlArena })));

import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CoinCollector } from './components/CoinCollector';

// Small components — keep direct
import { ProWelcomeModal } from './components/ProWelcomeModal';
import { StreakModal } from './components/StreakModal';
import { CreatorGiftModal } from './components/CreatorGiftModal';
import { AchievementPopupManager } from './components/AchievementPopupManager';

import { ProGateOverlay } from './components/ProGateOverlay';

// Loading fallback for lazy routes
const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-surface-light dark:bg-surface-dark">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const ProtectedRoute = ({ children }: React.PropsWithChildren) => {
  const { isAuthenticated, isAuthLoading } = useApp();

  // Show nothing while restoring session
  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-light dark:bg-surface-dark">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// PageLayer component now removes the inactive page from document flow using `hidden` to fix the view duplication bug
const PageLayer = ({ active, children, zIndex = 0 }: { active: boolean; children: React.ReactNode; zIndex?: number }) => (
  <div
    className={`absolute inset-0 transition-opacity duration-300 ${active ? 'opacity-100 pointer-events-auto flex' : 'opacity-0 pointer-events-none hidden'}`}
    style={{ zIndex }}
  >
    {children}
  </div>
);


const AppRoutes = () => {
  const { isAuthLoading, isAuthenticated, user, isPro, recordLoginStreak, setIsStreakModalOpen } = useApp();
  const location = useLocation();
  const path = location.pathname;

  // Streak Modal State
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [checkinResult, setCheckinResult] = useState<any>(null);
  const [isStreakRecovery, setIsStreakRecovery] = useState(false);

  // Creator Gift State
  const [showMegaGiftModal, setShowMegaGiftModal] = useState(false);

  // --- Handle Login Streak (Global Trigger — separate from manual check-in) ---
  useEffect(() => {
    const doLoginStreak = async () => {
      if (!isAuthenticated || !user?.id) return;

      // Only trigger on "Main" entry pages (Dashboard or Practice) 
      const isEntryPage = path === '/dashboard' || path === '/' || path === '/practice';
      if (!isEntryPage) return;

      try {
        const hasLoggedToday = sessionStorage.getItem('login_streak_today');
        if (hasLoggedToday) return;

        const result = await recordLoginStreak();

        if (result.success) {
          sessionStorage.setItem('login_streak_today', 'true');
          setStreakCount(result.streak || 1);
          setIsStreakRecovery(false);
          setCheckinResult({
            basePoints: result.points || 10,
            bonusPoints: 0,
            totalPoints: result.points || 10,
            isMilestone: false,
            alreadyCheckedIn: false,
          });
          setIsStreakModalOpen(true);
          setShowStreakModal(true);
        } else if (result.reason === 'already_logged_today') {
          sessionStorage.setItem('login_streak_today', 'true');
        }
      } catch (err) {
        console.error('Login streak exception:', err);
      }
    };

    doLoginStreak();
  }, [isAuthenticated, user?.id, path, recordLoginStreak]);



  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-light dark:bg-surface-dark">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }


  // Define Main Pages
  const isDashboard = path === '/dashboard' || path === '/';
  const isPractice = path === '/practice';
  const isAnalysis = path === '/analysis';
  const isForum = path.startsWith('/forum');
  const isSettings = path === '/settings';

  // Check if we are on any main page
  const isOnMainPage = isDashboard || isPractice || isAnalysis || isForum || isSettings;
  const isPracticeSession = path.startsWith('/practice/session') || path.startsWith('/practice/unit/');

  return (
    <div className={`h-[100dvh] w-full bg-background-light dark:bg-background-dark overflow-x-hidden overflow-y-hidden relative ${isPracticeSession ? '' : 'min-w-[360px]'}`}>


      {/* Persistent Page Layers (Main 5) */}
      <>
        <PageLayer active={isDashboard} zIndex={isDashboard ? 40 : 10}>
          <Dashboard />
        </PageLayer>

        <PageLayer active={isPractice} zIndex={isPractice ? 40 : 10}>
          <PracticeHub />
        </PageLayer>

        {isAuthenticated && (
          <>
            <PageLayer active={isAnalysis} zIndex={isAnalysis ? 40 : 10}>
              {(!isPro && isAnalysis) ? <ProGateOverlay featureName="Analysis" /> : <Analysis />}
            </PageLayer>

            <PageLayer active={isForum} zIndex={isForum ? 50 : 10}>
              {(!isPro && isForum) ? <ProGateOverlay featureName="Forum" /> : <Forum />}
            </PageLayer>

            <PageLayer active={isSettings} zIndex={isSettings ? 40 : 10}>
              <Settings />
            </PageLayer>
          </>
        )}
      </>

      {/* Redirect to login if unauthenticated and on a main page that REQUIRES auth */}
      {!isAuthenticated && (isAnalysis || isForum || isSettings) && <Navigate to="/login" replace />}

      {/* Sub-Routes & Non-Main Pages Layer (Always visible if NOT on a main page layer) */}
      <div className={`absolute inset-0 z-[60] overflow-y-auto transition-opacity duration-300 ${isOnMainPage ? 'opacity-0 pointer-events-none hidden' : 'opacity-100 pointer-events-auto flex flex-col'}`}>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            {/* Auth Pages (Always visible if not on main page) */}
            {!isAuthenticated && (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
              </>
            )}

            {/* Sub-pages and Details (Overlay on main layers) */}
            <Route path="/practice/unit/:unitId" element={<TopicDetail />} />
            <Route path="/practice/session" element={<Practice />} />
            <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
            <Route path="/prestige" element={<ProtectedRoute><PrestigePage /></ProtectedRoute>} />
            <Route path="/stardust" element={<ProtectedRoute><StardustPage /></ProtectedRoute>} />
            <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/points" element={<ProtectedRoute><PointsPage /></ProtectedRoute>} />
            <Route path="/checkin" element={<ProtectedRoute><CheckinPage /></ProtectedRoute>} />
            <Route path="/wrong-answers" element={<ProtectedRoute><WrongAnswerBook /></ProtectedRoute>} />
            <Route path="/textbooks/:courseType/:unitNumber" element={<TextbookViewer />} />

            {/* Settings Subpages */}
            <Route path="/settings/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
            <Route path="/settings/security" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
            <Route path="/settings/subscription" element={<ProtectedRoute><SubscriptionSettings /></ProtectedRoute>} />
            <Route path="/settings/creator" element={<ProtectedRoute><QuestionCreator /></ProtectedRoute>} />
            <Route path="/debug-qa" element={<ProtectedRoute><DebugQA /></ProtectedRoute>} />

            {/* Lobby & Games */}
            <Route path="/lobby" element={<Lobby />} />
            <Route path="/games" element={<GameHub />} />
            <Route path="/games/match3" element={<MatchGameWrapper onBack={() => window.history.back()} />} />
            <Route path="/games/snake" element={<SnakeGame />} />
            <Route path="/games/2048" element={<Game2048 />} />
            <Route path="/games/tetris" element={<TetrisGame />} />
            <Route path="/games/memory" element={<MemoryGame />} />
            <Route path="/games/minesweeper" element={<MinesweeperGame />} />
            <Route path="/games/neon-knight" element={<NeonKnight />} />
            <Route path="/games/defenders" element={<GardenDefenders />} />
            <Route path="/games/sky-roller" element={<SkyRoller />} />
            <Route path="/games/brawl-arena" element={<BrawlArena />} />

            {/* Static Pages */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/support" element={<Support />} />

            {/* Catch-all to make the URL reflect the state, though the layers handle visibility */}
            <Route path="/dashboard" element={<div />} />
            <Route path="/practice" element={<div />} />
            <Route path="/analysis" element={<div />} />
            <Route path="/forum" element={<div />} />
            <Route path="/settings" element={<div />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </div>

      {/* Global Daily Streak Modal */}
      <StreakModal
        isOpen={showStreakModal}
        streak={streakCount}
        onClose={() => { setShowStreakModal(false); setIsStreakModalOpen(false); }}
        isRecovery={isStreakRecovery}
        checkinResult={checkinResult}
      />
    </div>
  );
};

const App = () => {
  return (
    <ToastProvider>
      <ErrorBoundary>
        <AppProvider>
          <Router>
            <CoinCollector />

            <AppRoutes />
            <ProWelcomeModal />
            <AchievementPopupManager />
          </Router>
        </AppProvider>
      </ErrorBoundary>
    </ToastProvider>
  );
};

export default App;